import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { adminLogin, adminStats, adminLeads, adminNewsletter, adminExportUrl, API } from "../lib/api";
import { toast } from "sonner";
import { Loader2, LogOut, Download, Send, Users, Calendar, Mail, Search, RefreshCcw, Eye, UserCheck } from "lucide-react";

const TOKEN_KEY = "bbs_admin_token";

function useAdminToken() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const save = (t) => {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
    setToken(t || "");
  };
  return [token, save];
}

function LoginCard({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await adminLogin(password);
      onLogin(res.token);
      toast.success("Willkommen zurück.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#09090b] px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-md admin-card rounded-md p-8"
        data-testid="admin-login-form"
      >
        <div className="font-mono-accent text-[10px] uppercase tracking-[0.35em] text-zinc-500">
          bestburgerstuttgart.de · admin
        </div>
        <h1 className="mt-2 font-display uppercase text-4xl text-white tracking-tight">Zutritt</h1>
        <p className="mt-1 text-sm text-zinc-400">Nur für Team-Members.</p>

        <label className="mt-6 block text-xs font-mono-accent uppercase tracking-[0.25em] text-zinc-500">
          Passwort
        </label>
        <input
          type="password"
          data-testid="admin-password-input"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-2 w-full h-12 px-4 rounded-md bg-[#09090b] border border-[#27272a] text-white focus:outline-none focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30]"
          autoFocus
          required
        />

        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-button"
          className="btn-primary w-full mt-6 h-12 rounded-md flex items-center justify-center text-lg"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "EINLOGGEN"}
        </button>

        <div className="mt-6 text-xs text-zinc-500">
          <Link to="/" className="hover:text-zinc-300" data-testid="back-to-site">
            ← zurück zur Seite
          </Link>
        </div>
      </form>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent = "#FF3B30", testId }) {
  return (
    <div className="admin-card rounded-md p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-zinc-500">
          {label}
        </div>
        <Icon className="w-4 h-4" style={{ color: accent }} />
      </div>
      <div className="mt-3 font-display text-5xl text-white tracking-tight">{value}</div>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const [newsSubject, setNewsSubject] = useState("Winner Winner Winner 🎉");
  const [newsBody, setNewsBody] = useState(
    "<h1>Hey!</h1>\n<p>Kleines Update von BUDDIEZ buns &amp; rolls - deinem Stuttgart Burger-Winner.</p>\n<p>Komm vorbei: Werderstr. 79, 70190 Stuttgart.</p>"
  );
  const [sending, setSending] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([adminStats(), adminLeads(query)]);
      setStats(s);
      setLeads(l.items || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session abgelaufen.");
        onLogout();
      } else {
        toast.error("Fehler beim Laden.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const l = await adminLeads(query);
      setLeads(l.items || []);
    } catch {
      toast.error("Suche fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = async () => {
    try {
      const res = await fetch(adminExportUrl(), { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV heruntergeladen.");
    } catch (err) {
      toast.error("CSV-Export fehlgeschlagen.");
    }
  };

  const sendNewsletter = async () => {
    if (!newsSubject.trim() || !newsBody.trim()) {
      toast.error("Betreff & Inhalt sind Pflicht.");
      return;
    }
    if (!window.confirm(`Newsletter wirklich an ${stats?.total ?? "alle"} Empfänger senden?`)) return;
    setSending(true);
    try {
      const res = await adminNewsletter(newsSubject, newsBody, true);
      if (res.email_mocked) {
        toast.message("Test-Modus", {
          description: `Empfänger: ${res.recipients}. Versand gemockt (kein API-Key).`,
        });
      } else {
        toast.success(`Versendet: ${res.sent}/${res.recipients} (fehlgeschlagen: ${res.failed})`);
      }
    } catch (err) {
      toast.error("Versand fehlgeschlagen.");
    } finally {
      setSending(false);
    }
  };

  const fmt = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  const emailMocked = useMemo(
    () => leads.some((l) => l.email_mocked) || (leads.length === 0 && false),
    [leads]
  );

  return (
    <div className="min-h-screen bg-[#09090b] text-white">
      <header className="border-b border-zinc-900 px-6 lg:px-10 py-5 flex items-center justify-between">
        <div>
          <div className="font-mono-accent text-[10px] uppercase tracking-[0.35em] text-zinc-500">
            bestburgerstuttgart.de · admin
          </div>
          <h1 className="font-display uppercase text-3xl tracking-tight">Dashboard</h1>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={refresh}
            className="flex items-center gap-2 text-xs font-mono-accent uppercase tracking-[0.25em] text-zinc-400 hover:text-white transition-colors"
            data-testid="refresh-button"
          >
            <RefreshCcw className="w-4 h-4" /> refresh
          </button>
          <button
            onClick={onLogout}
            className="flex items-center gap-2 text-xs font-mono-accent uppercase tracking-[0.25em] text-zinc-400 hover:text-white transition-colors"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4" /> logout
          </button>
        </div>
      </header>

      <main className="p-6 lg:p-10 space-y-8">
        {/* Stats */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stats-grid">
          <StatCard icon={Users} label="Total Leads" value={stats?.total ?? "—"} testId="stat-total" />
          <StatCard icon={Calendar} label="Leads Heute" value={stats?.today ?? "—"} accent="#FFD60A" testId="stat-today" />
          <StatCard icon={Calendar} label="Leads 7 Tage" value={stats?.last_7_days ?? "—"} accent="#32ADE6" testId="stat-week" />
          <StatCard icon={Mail} label="Mails gesendet" value={stats?.email_sent ?? "—"} accent="#34C759" testId="stat-emails" />
        </section>

        {/* Pageview stats */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="pv-grid">
          <StatCard icon={Eye} label="Seitenaufrufe Total" value={stats?.page_views_total ?? "—"} accent="#E8B14A" testId="stat-pv-total" />
          <StatCard icon={Eye} label="Aufrufe Heute" value={stats?.page_views_today ?? "—"} accent="#E8B14A" testId="stat-pv-today" />
          <StatCard icon={Eye} label="Aufrufe 7 Tage" value={stats?.page_views_7d ?? "—"} accent="#E8B14A" testId="stat-pv-7d" />
          <StatCard icon={UserCheck} label="Unique Besucher" value={stats?.unique_visitors_total ?? "—"} accent="#AF52DE" testId="stat-unique" />
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Leads */}
          <div className="lg:col-span-2 admin-card rounded-md p-5" data-testid="leads-panel">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
              <div>
                <h2 className="font-display uppercase text-2xl tracking-tight">Leads</h2>
                <p className="text-xs text-zinc-500">{leads.length} von {stats?.total ?? 0} angezeigt</p>
              </div>
              <div className="flex items-center gap-2">
                <form onSubmit={onSearch} className="relative">
                  <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="E-Mail suchen..."
                    className="h-10 pl-9 pr-3 rounded-md bg-[#09090b] border border-[#27272a] text-sm text-white focus:outline-none focus:border-[#FF3B30]"
                    data-testid="search-input"
                  />
                </form>
                <button
                  onClick={exportCsv}
                  className="flex items-center gap-2 h-10 px-4 rounded-md border border-[#27272a] bg-[#09090b] text-sm text-white hover:border-[#FF3B30] transition-colors"
                  data-testid="export-button"
                >
                  <Download className="w-4 h-4" /> CSV
                </button>
              </div>
            </div>

            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] font-mono-accent uppercase tracking-[0.25em] text-zinc-500 border-b border-zinc-900">
                    <th className="py-3 px-2">E-Mail</th>
                    <th className="py-3 px-2">Datum / Uhrzeit</th>
                    <th className="py-3 px-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={3} className="py-12 text-center text-zinc-500">
                        <Loader2 className="w-5 h-5 animate-spin inline" />
                      </td>
                    </tr>
                  ) : leads.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="py-12 text-center text-zinc-500">
                        Noch keine Leads.
                      </td>
                    </tr>
                  ) : (
                    leads.map((l) => (
                      <tr key={l.id} className="border-b border-zinc-900/60 hover:bg-[#1c1c24]/50 transition-colors" data-testid="lead-row">
                        <td className="py-3 px-2 text-white font-medium break-all">{l.email}</td>
                        <td className="py-3 px-2 text-zinc-400">{fmt(l.created_at)}</td>
                        <td className="py-3 px-2">
                          {l.email_sent ? (
                            <span className="inline-block px-2 py-1 rounded-sm text-[10px] font-mono-accent uppercase tracking-[0.2em] bg-[#34C759]/15 text-[#34C759] border border-[#34C759]/30">
                              gesendet
                            </span>
                          ) : l.email_mocked ? (
                            <span className="inline-block px-2 py-1 rounded-sm text-[10px] font-mono-accent uppercase tracking-[0.2em] bg-[#FFD60A]/15 text-[#FFD60A] border border-[#FFD60A]/30">
                              mocked
                            </span>
                          ) : (
                            <span className="inline-block px-2 py-1 rounded-sm text-[10px] font-mono-accent uppercase tracking-[0.2em] bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/30">
                              fehler
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Newsletter */}
          <div className="admin-card rounded-md p-5" data-testid="newsletter-panel">
            <h2 className="font-display uppercase text-2xl tracking-tight">Newsletter</h2>
            <p className="text-xs text-zinc-500">Broadcast an alle Opt-In Leads.</p>

            <label className="mt-4 block text-[10px] font-mono-accent uppercase tracking-[0.25em] text-zinc-500">
              Betreff
            </label>
            <input
              value={newsSubject}
              onChange={(e) => setNewsSubject(e.target.value)}
              className="mt-2 w-full h-10 px-3 rounded-md bg-[#09090b] border border-[#27272a] text-white text-sm focus:outline-none focus:border-[#FF3B30]"
              data-testid="newsletter-subject"
            />

            <label className="mt-4 block text-[10px] font-mono-accent uppercase tracking-[0.25em] text-zinc-500">
              HTML-Inhalt
            </label>
            <textarea
              rows={8}
              value={newsBody}
              onChange={(e) => setNewsBody(e.target.value)}
              className="mt-2 w-full px-3 py-2 rounded-md bg-[#09090b] border border-[#27272a] text-white text-sm font-mono-accent focus:outline-none focus:border-[#FF3B30]"
              data-testid="newsletter-body"
            />

            <button
              onClick={sendNewsletter}
              disabled={sending}
              className="btn-primary w-full mt-4 h-11 rounded-md flex items-center justify-center gap-2 text-sm disabled:opacity-60"
              data-testid="newsletter-send"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> VERSENDEN</>}
            </button>

            <p className="mt-3 text-[10px] text-zinc-500 leading-relaxed">
              Nur an Empfänger mit Datenschutz-Zustimmung. Bei leerem Resend-Key wird der Versand automatisch gemockt.
            </p>
          </div>
        </section>

        {/* Footer info */}
        <footer className="text-xs text-zinc-600 pt-4">
          API: {API} · Token gültig 12h.
        </footer>
      </main>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useAdminToken();
  const navigate = useNavigate();

  if (!token) {
    return <LoginCard onLogin={(t) => setToken(t)} />;
  }
  return <Dashboard token={token} onLogout={() => { setToken(""); navigate("/admin"); }} />;
}
